/**
 * 屏幕自适应工具（PC 管理端 + 大屏）
 * ----------------------------------------------------------------
 *  双轨方案:
 *  ① CSS zoom = 屏幕宽度 / 1920   → 等比缩放布局（不设下限，4K 封顶 2.0）
 *  ② html font-size = 目标视觉字号 / zoom  → 让 rem/em 组件精确达标
 *
 *  目标视觉字号（最终用户看到的）:
 *    14" 屏 (≈1280px) → 12px
 *    32" 屏 (≈2560px) → 22px
 *    中间线性插值
 *
 *  原理: zoom 会把一切都缩一下，所以 html font-size 要反过来补偿:
 *    视觉字号 = html font-size × zoom
 *    所以 html font-size = 目标视觉字号 / zoom
 *
 *  限制: 项目里写死 font-size: 14px !important 的内联样式/自定义组件
 *        只受 zoom 控制（14" 时 ≈ 9.3px），无法精确到 12px。
 *        Element Plus 组件大量用 rem，能精确达标。
 * ----------------------------------------------------------------
 *  跳过场景: Login / 移动端 H5 / 协议页 —— 完全不缩放
 *
 *  用法: App.vue onMounted 调 initAutofit()
 */

const DESIGN_WIDTH = 1920
const MAX_ZOOM = 2.0

// —— 目标视觉字号映射 ——
const MIN_SCREEN_W  = 1280   // 14" 笔记本典型 CSS 宽度
const MAX_SCREEN_W  = 2560   // 32" 显示器典型 CSS 宽度
const TARGET_MIN_FONT = 12   // 14" 时用户看到的字号
const TARGET_MAX_FONT = 22   // 32" 时用户看到的字号

const SKIP_PATHS = [
  /^\/$/,
  /^\/login/,
  /^\/privacy/,
  /^\/agreement/,
  /^\/empower/,
  /^\/empowerlogin/,
  /^\/empowerregister/,
  /^\/alarmform/,
  /^\/offlinedetail/,
  /^\/onlinedetail/,
  /^\/offlinedevice/,
  /^\/onlinedevice/,
  /^\/commuting/,
]

function _shouldSkip() {
  const p = window.location.pathname
  return SKIP_PATHS.some(re => re.test(p))
}

let _cleanupFns = []

export function initAutofit() {
  const app = document.getElementById('app')
  if (!app) {
    console.warn('[autofit] #app 节点尚未挂载, 跳过初始化')
    return
  }

  function update() {
    const w = window.innerWidth

    if (_shouldSkip()) {
      app.style.zoom = ''
      document.documentElement.style.fontSize = ''
      return
    }

    // ① zoom —— 布局等比缩放
    const zoom = Math.min(MAX_ZOOM, w / DESIGN_WIDTH)
    app.style.zoom = zoom

    // ② html font-size —— 精确补偿 rem/em 组件的视觉字号
    // 先算目标视觉字号，再除以 zoom 得到真实设置值
    const t = Math.max(0, Math.min(1,
      (w - MIN_SCREEN_W) / (MAX_SCREEN_W - MIN_SCREEN_W)
    ))
    const targetVisual = TARGET_MIN_FONT + t * (TARGET_MAX_FONT - TARGET_MIN_FONT)
    document.documentElement.style.fontSize = (targetVisual / zoom) + 'px'
  }

  update()
  window.addEventListener('resize', update)
  window.addEventListener('orientationchange', update)

  _cleanupFns.push(() => {
    window.removeEventListener('resize', update)
    window.removeEventListener('orientationchange', update)
  })
}

export function destroyAutofit() {
  _cleanupFns.forEach(fn => fn())
  _cleanupFns = []
  const app = document.getElementById('app')
  if (app) app.style.zoom = ''
  document.documentElement.style.fontSize = ''
}
